/**
 * Admin → Commissions tab.
 *
 * Read-only list of recent commission payouts with a retry button for
 * failed ones. Ported from `routes/admin.ts`.
 */
import { useEffect, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { Spinner } from '../../components/Spinner';

interface Commission {
  id: string;
  createdAt: string;
  affiliate: { id: string; name: string };
  type: 'setup' | 'recurring' | string;
  paymentAmount: number;
  commissionAmount: number;
  rate: number;
  status: 'completed' | 'failed' | string;
  error?: string | null;
}

export function AdminCommissions() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api('/admin/commissions?limit=50');
      if (!r.ok) throw new Error('Failed to load commissions');
      const data = await r.json() as { commissions?: Commission[] };
      setCommissions(data.commissions || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function retry(id: string) {
    try {
      const r = await api('/admin/commissions/' + id + '/retry', { method: 'POST' });
      const result = await r.json() as { error?: string; message?: string };
      if (!r.ok) throw new Error(result.error || 'Failed to retry');
      showToast('success', 'Transfer Initiated', result.message || 'Retry initiated');
      load();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  return (
    <AdminShell active="commissions">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">Commissions</h2>
      </div>

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-x-auto">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading commissions...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !commissions.length ? (
          <div class="p-8 text-center text-slate-400">No commissions yet</div>
        ) : (
          <table class="w-full">
            <thead class="bg-dark-700">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Affiliate</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Payment</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Commission</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Error</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-700/50">
              {commissions.map((c) => {
                const typeCls = c.type === 'setup' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400';
                const statusCls = c.status === 'completed'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : c.status === 'failed'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-amber-500/20 text-amber-400';
                const errTrimmed = c.error ? (c.error.substring(0, 50) + (c.error.length > 50 ? '...' : '')) : '-';
                return (
                  <tr class="hover:bg-dark-700/50">
                    <td class="px-4 py-3 text-sm text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td class="px-4 py-3">
                      <div class="text-sm text-slate-200">{c.affiliate.name}</div>
                    </td>
                    <td class="px-4 py-3">
                      <span class={`text-xs px-2 py-1 rounded ${typeCls}`}>{c.type}</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-slate-300">${c.paymentAmount.toFixed(2)}</td>
                    <td class="px-4 py-3 text-sm text-emerald-400">${c.commissionAmount.toFixed(2)} ({c.rate}%)</td>
                    <td class="px-4 py-3">
                      <span class={`text-xs px-2 py-1 rounded ${statusCls}`}>{c.status}</span>
                    </td>
                    <td class="px-4 py-3 text-xs text-red-400 max-w-xs truncate" title={c.error || ''}>
                      {errTrimmed}
                    </td>
                    <td class="px-4 py-3">
                      {c.status === 'failed' && (
                        <button type="button" onClick={() => retry(c.id)} class="text-cyan-400 hover:text-cyan-300 text-sm">Retry</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}

export default AdminCommissions;
